import React, { PureComponent } from "react";
import PropTypes from "prop-types";
import { MdClose } from "react-icons/md";
import OLMap from "ol/Map";
import Feature from "ol/Feature";
import { getCenter } from "ol/extent";
import { unByKey } from "ol/Observable";
const propTypes = {
  children: PropTypes.node.isRequired,
  map: PropTypes.instanceOf(OLMap).isRequired,
  feature: PropTypes.instanceOf(Feature),
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  panIntoView: PropTypes.bool,
  panRect: PropTypes.objectOf(PropTypes.number),
  popupCoordinate: PropTypes.arrayOf(PropTypes.number),
  className: PropTypes.string,
  titles: PropTypes.shape({
    closeButton: PropTypes.string
  }),
  onCloseClick: PropTypes.func,
  tabIndex: PropTypes.string,
  renderHeader: PropTypes.func,
  renderCloseButton: PropTypes.func,
  renderFooter: PropTypes.func
};
const defaultProps = {
  header: null,
  feature: null,
  panIntoView: false,
  panRect: null,
  popupCoordinate: null,
  className: "rs-popup",
  tabIndex: "",
  titles: { closeButton: "Close" },
  onCloseClick: () => {
  },
  renderHeader: null,
  renderCloseButton: null,
  renderFooter: () => {
    return null;
  }
};
class Popup extends PureComponent {
  static renderHeader(props) {
    const { header, renderCloseButton } = props;
    return /* @__PURE__ */ React.createElement("div", {
      className: "rs-popup-header"
    }, header, (renderCloseButton || Popup.renderCloseButton)(props));
  }
  static renderCloseButton({ onCloseClick, titles }) {
    return /* @__PURE__ */ React.createElement("div", {
      role: "button",
      tabIndex: 0,
      className: "rs-popup-close-bt",
      title: titles.closeButton,
      "aria-label": titles.closeButton,
      onClick: () => {
        return onCloseClick();
      },
      onKeyPress: (evt) => {
        return evt.which === 13 && onCloseClick();
      }
    }, /* @__PURE__ */ React.createElement(MdClose, {
      focusable: false
    }));
  }
  constructor(props) {
    super(props);
    this.state = {
      popupElement: null,
      top: 0,
      left: 0
    };
    this.postrenderKey = null;
  }
  componentDidMount() {
    const { map } = this.props;
    this.updatePixelPosition();
    this.postrenderKey = map.on("postrender", () => {
      this.updatePixelPosition();
    });
  }
  componentDidUpdate(prevProps, prevState) {
    const { feature, panIntoView, popupCoordinate } = this.props;
    const { popupElement } = this.state;
    if (feature !== prevProps.feature || popupCoordinate !== prevProps.popupCoordinate) {
      this.updatePixelPosition();
    }
    if (panIntoView && popupElement && popupElement !== prevState.popupElement) {
      this.panIntoView();
    }
  }
  componentWillUnmount() {
    unByKey(this.postrenderKey);
  }
  panIntoView() {
    const { map, panRect } = this.props;
    const { popupElement } = this.state;
    const mapRect = panRect || map.getTarget().getBoundingClientRect();
    const popupRect = popupElement.getBoundingClientRect();
    const [x, y] = map.getView().getCenter();
    const res = map.getView().getResolution();
    const newCenter = [x, y];
    if (mapRect.top > popupRect.top) {
      newCenter[1] = y + (mapRect.top - popupRect.top) * res;
    }
    if (mapRect.left > popupRect.left) {
      newCenter[0] = x - (mapRect.left - popupRect.left) * res;
    }
    if (mapRect.right < popupRect.right) {
      newCenter[0] = x + (popupRect.right - mapRect.right) * res;
    }
    if (mapRect.bottom < popupRect.bottom) {
      newCenter[1] = y - (popupRect.bottom - mapRect.bottom) * res;
    }
    if (newCenter[0] !== x || newCenter[1] !== y) {
      map.getView().animate({ center: newCenter, duration: 500 });
    }
  }
  updatePixelPosition() {
    const { map, feature, popupCoordinate } = this.props;
    let coord = popupCoordinate;
    if (feature && !coord) {
      coord = getCenter(feature.getGeometry().getExtent());
    }
    if (coord) {
      const pos = map.getPixelFromCoordinate(coord);
      if (pos && pos.length === 2) {
        this.setState({
          left: pos[0],
          top: pos[1]
        });
      }
    }
  }
  render() {
    const {
      feature,
      popupCoordinate,
      children,
      header,
      titles,
      tabIndex,
      renderHeader,
      renderFooter,
      ...other
    } = this.props;
    if (!feature && !popupCoordinate) {
      return null;
    }
    delete other.panIntoView;
    delete other.panRect;
    delete other.map;
    delete other.header;
    delete other.onCloseClick;
    delete other.renderCloseButton;
    const { top, left } = this.state;
    const key = feature ? feature.getId() : popupCoordinate.join();
    return /* @__PURE__ */ React.createElement("div", {
      className: "rs-popup",
      style: {
        left,
        top
      },
      ...other
    }, /* @__PURE__ */ React.createElement("div", {
      className: "rs-popup-container",
      tabIndex,
      key,
      ref: (popupElement) => {
        this.setState({ popupElement });
      }
    }, (renderHeader || Popup.renderHeader)(this.props), /* @__PURE__ */ React.createElement("div", {
      className: "rs-popup-body"
    }, children), renderFooter(this.props)));
  }
}
Popup.propTypes = propTypes;
Popup.defaultProps = defaultProps;
export default Popup;
